import { useCallback, useEffect, useRef, useState } from "react";
import type { InputParam } from "../types/grid.ts";
import { encodeFilters, decodeFilters } from "../util/filterUrlCodec.ts";

// ── Settings ──────────────────────────────────────────────
// Session state  : default ON. Filter state persists in sessionStorage.
//                  When OFF, filter state is encoded in the URL hash instead
//                  (the inverse behaviour — useful for sharing URLs).
// Per-page state : sub-setting (only usable when Session state is on).
//                  When on, each page keeps its own state across SPA navigation
//                  (per-page storage keys). When off, state is shared by a single
//                  blob that's cleared on route change (so only survives reload).

const LS_SESSION_KEY = "webadmincc-session-state";
const LS_PAGE_KEY = "webadmincc-page-state";

const SESSION_BLOB_KEY = "webadmincc-pagestate";
const SESSION_PAGE_PREFIX = "webadmincc-pagestate-";

/** Master toggle — default ON (sessionStorage mode). Returns false only if explicitly disabled. */
export function isSessionStateEnabled(): boolean {
  return localStorage.getItem(LS_SESSION_KEY) !== "false";
}

/** Per-page persistence sub-setting. Default OFF. Only meaningful when session state is on. */
export function isPageStateEnabled(): boolean {
  return localStorage.getItem(LS_PAGE_KEY) === "true";
}

/**
 * Enable/disable the master session state setting.
 * When disabling (switching to URL hash mode), clears the sessionStorage blobs
 * since they're no longer the source of truth.
 */
export function setSessionStateEnabled(enabled: boolean): void {
  localStorage.setItem(LS_SESSION_KEY, String(enabled));
  if (!enabled) {
    clearAllStoredState();
  } else if (window.location.hash) {
    // Switching to sessionStorage mode — clean up any hash left in the URL.
    const url = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", url);
  }
}

/**
 * Enable/disable the per-page persistence sub-setting.
 * When disabling, removes all per-page keys.
 */
export function setPageStateEnabledSetting(enabled: boolean): void {
  localStorage.setItem(LS_PAGE_KEY, String(enabled));
  if (!enabled) {
    clearPerPageStoredState();
  }
}

// ── Storage helpers ────────────────────────────────────────

export type PageStateBlob = Record<string, unknown>;

/** Derive a per-page storage key from the current location (excluding in-page params like edit=). */
function getPageKey(pathname?: string, search?: string): string {
  const path = pathname ?? window.location.pathname;
  const rawSearch = search ?? window.location.search;
  // Strip in-page parameters that shouldn't affect the page identity
  const params = new URLSearchParams(rawSearch);
  params.delete("edit");
  const normalized = params.toString();
  return SESSION_PAGE_PREFIX + path + (normalized ? "?" + normalized : "");
}

/** Current sessionStorage key (depends on Page state setting). */
function getStorageKey(): string {
  return isPageStateEnabled() ? getPageKey() : SESSION_BLOB_KEY;
}

function readSessionBlob(): PageStateBlob {
  try {
    const raw = sessionStorage.getItem(getStorageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionBlob(blob: PageStateBlob): void {
  const key = getStorageKey();
  if (Object.keys(blob).length === 0) {
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(blob));
}

/** Remove the shared blob and every per-page blob. */
function clearAllStoredState(): void {
  try {
    sessionStorage.removeItem(SESSION_BLOB_KEY);
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SESSION_PAGE_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) sessionStorage.removeItem(k);
  } catch { /* ignore */ }
}

/** Remove only the per-page blobs, keeping the shared one (if any). */
function clearPerPageStoredState(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SESSION_PAGE_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) sessionStorage.removeItem(k);
  } catch { /* ignore */ }
}

// ── URL hash helpers (used when Session state is OFF) ──────

function readHashBlob(): PageStateBlob {
  const hash = window.location.hash.slice(1);
  if (!hash) return {};
  const params = new URLSearchParams(hash);
  const blob: PageStateBlob = {};
  for (const [k, v] of params.entries()) {
    if (k.startsWith("f.")) {
      const decoded = decodeFilters(v);
      if (decoded) blob[k] = decoded;
    } else {
      blob[k] = v;
    }
  }
  return blob;
}

function writeHashBlob(blob: PageStateBlob, push: boolean): void {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(blob)) {
    if (k.startsWith("f.") && Array.isArray(v)) {
      const encoded = encodeFilters(v as InputParam[]);
      if (encoded) params.set(k, encoded);
    } else if (typeof v === "string" && v) {
      params.set(k, v);
    }
  }
  const str = params.toString();
  const hash = str ? `#${str}` : "";
  const url = window.location.pathname + window.location.search + hash;
  if (push) window.history.pushState(null, "", url);
  else window.history.replaceState(null, "", url);
}

// ── Navigation cleanup ────────────────────────────────────

/**
 * Clear state on SPA navigation. Called from MainLayout when the user moves
 * to a different page.
 *
 * Only applies to the shared sessionStorage blob:
 * - Session state OFF (URL hash mode) → no-op (new page has its own URL)
 * - Per-page state ON → no-op (each page keeps its own state)
 * - Otherwise: remove the shared blob so the next page starts fresh
 *
 * Reload is not affected because browser reload doesn't trigger this path.
 */
export function clearPageStateOnNavigation(): void {
  if (!isSessionStateEnabled()) return;
  if (isPageStateEnabled()) return;
  try { sessionStorage.removeItem(SESSION_BLOB_KEY); } catch { /* ignore */ }
}

/** True when this page load is a reload (as opposed to first visit / SPA navigation). */
function isPageReload(): boolean {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === "reload";
  } catch {
    return false;
  }
}

let pageLoadCleaned = false;
function ensurePageLoadCleanup(): void {
  if (pageLoadCleaned) return;
  pageLoadCleaned = true;
  if (!isSessionStateEnabled()) return;

  // If the URL carries a hash with state (e.g. from a bookmarked link), migrate
  // it into sessionStorage then strip the hash. The hash is a temporary
  // transport — session-storage remains the user's configured persistence.
  const hash = window.location.hash.slice(1);
  if (hash) {
    const blob = readHashBlob();
    if (Object.keys(blob).length > 0) {
      writeSessionBlob(blob);
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", url);
      return;
    }
  }

  if (!isPageReload()) {
    try { sessionStorage.removeItem(SESSION_BLOB_KEY); } catch { /* ignore */ }
  }
}

/**
 * Write the current page state into `location.hash` so the browser's address
 * bar reflects a bookmarkable URL. User then bookmarks via Ctrl+D / cmd+D.
 *
 * - URL mode: state already lives in the hash; no-op.
 * - Session mode: reads the current sessionStorage blob and serialises it
 *   into a hash fragment. The user's configured persistence (session vs
 *   URL) is untouched — hash is a temporary transport that the next load
 *   will consume and strip.
 */
export function exposeStateInUrl(): void {
  if (!isSessionStateEnabled()) return;
  const blob = readSessionBlob();
  const hash = buildHashFromPageState(blob);
  const url = window.location.pathname + window.location.search + hash;
  window.history.replaceState(null, "", url);
}

/**
 * Write the full page state blob. Used by the shared link resolver before
 * navigating to the target page. Writes to sessionStorage (Session state on)
 * or is a no-op (Session state off — the caller should navigate with hash instead).
 */
export function writeFullPageState(blob: PageStateBlob, targetPathname?: string, targetSearch?: string): void {
  if (!isSessionStateEnabled()) return;
  if (Object.keys(blob).length === 0) return;
  const key = isPageStateEnabled() && targetPathname
    ? getPageKey(targetPathname, targetSearch ?? "")
    : SESSION_BLOB_KEY;
  sessionStorage.setItem(key, JSON.stringify(blob));
}

/** Build a URL hash string from a page state blob (for URL hash mode shared link navigation). */
export function buildHashFromPageState(blob: PageStateBlob): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(blob)) {
    if (key.startsWith("f.") && Array.isArray(value)) {
      const encoded = encodeFilters(value as InputParam[]);
      if (encoded) params.set(key, encoded);
    } else if (typeof value === "string" && value) {
      params.set(key, value);
    }
  }
  const str = params.toString();
  return str ? `#${str}` : "";
}

// ── Hook ──────────────────────────────────────────────────

interface UseFilterUrlSyncOptions {
  gridName: string;
}

interface UseFilterUrlSyncResult {
  initialFilters: InputParam[] | null;
  syncFilters: (filters: InputParam[]) => void;
  pushFilters: (filters: InputParam[]) => void;
  /** Explicitly clear this grid's stored state (used by Clear button). */
  clearStoredFilters: () => void;
  popSignal: number;
  popFilters: InputParam[] | null;
}

/**
 * Persists grid filter state. Backend depends on user settings:
 *  - Session state ON (default): sessionStorage. Per-page keys if sub-setting on.
 *  - Session state OFF: URL hash (shareable URLs). Supports browser back/forward.
 */
export function useFilterUrlSync({ gridName }: UseFilterUrlSyncOptions): UseFilterUrlSyncResult {
  const useSession = isSessionStateEnabled();
  const filterKey = `f.${gridName}`;
  const [popSignal, setPopSignal] = useState(0);
  const [popFilters, setPopFilters] = useState<InputParam[] | null>(null);

  // Read initial filters on mount.
  const initialFilters = useRef<InputParam[] | null>(null);
  const initialRead = useRef(false);
  if (!initialRead.current) {
    initialRead.current = true;

    // Clear shared blob on fresh page load (not reload), session-state mode only
    ensurePageLoadCleanup();

    if (useSession) {
      const blob = readSessionBlob();
      const raw = blob[filterKey];
      if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
        initialFilters.current = raw as InputParam[];
      }
    } else {
      // URL hash mode
      const blob = readHashBlob();
      const raw = blob[filterKey];
      if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
        initialFilters.current = raw as InputParam[];
      }
    }
  }

  const writeFilters = useCallback((filters: InputParam[], push: boolean) => {
    // Only write non-empty state — never clear on empty so transient empty
    // states during mount (before external filters are applied) don't wipe
    // stored context. Next non-empty write will overwrite.
    if (filters.length === 0) return;

    if (useSession) {
      const blob = readSessionBlob();
      blob[filterKey] = filters;
      writeSessionBlob(blob);
    } else {
      const blob = readHashBlob();
      blob[filterKey] = filters;
      writeHashBlob(blob, push);
    }
  }, [useSession, filterKey]);

  const syncFilters = useCallback((filters: InputParam[]) => {
    writeFilters(filters, false);
  }, [writeFilters]);

  const pushFilters = useCallback((filters: InputParam[]) => {
    writeFilters(filters, true);
  }, [writeFilters]);

  const clearStoredFilters = useCallback(() => {
    if (useSession) {
      const blob = readSessionBlob();
      delete blob[filterKey];
      writeSessionBlob(blob);
    } else {
      const blob = readHashBlob();
      delete blob[filterKey];
      writeHashBlob(blob, false);
    }
  }, [useSession, filterKey]);

  // Listen for back/forward navigation (URL hash mode only)
  useEffect(() => {
    if (useSession) return;
    function onPopState() {
      const blob = readHashBlob();
      const raw = blob[filterKey];
      const next = (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object")
        ? (raw as InputParam[])
        : null;
      setPopFilters(next);
      setPopSignal(s => s + 1);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [useSession, filterKey]);

  return {
    initialFilters: initialFilters.current,
    syncFilters,
    pushFilters,
    clearStoredFilters,
    popSignal,
    popFilters,
  };
}

// ── Edit selection state (shared blob, `edit` sub-key) ──────

export function readEditSelection(): string | null {
  const blob = isSessionStateEnabled() ? readSessionBlob() : readHashBlob();
  const v = blob.edit;
  return typeof v === "string" ? v : null;
}

export function writeEditSelection(id: string | null): void {
  if (isSessionStateEnabled()) {
    const blob = readSessionBlob();
    if (id) blob.edit = id;
    else delete blob.edit;
    writeSessionBlob(blob);
  } else {
    const blob = readHashBlob();
    if (id) blob.edit = id;
    else delete blob.edit;
    writeHashBlob(blob, false);
  }
}
